import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Button, Box, Typography, Checkbox, FormGroup, FormControlLabel, Grid, Alert, CircularProgress
} from '@mui/material';
import api from '../../config/api';

const CompanyConnectorsDialog = ({ open, onClose, company, onSaved }) => {
  const [platforms, setPlatforms] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !company) return;
    setError('');
    setSelected(Array.isArray(company.enabled_platforms) ? [...company.enabled_platforms] : []);

    const fetchPlatforms = async () => {
      setLoading(true);
      try {
        // Super-admin endpoint — returns every platform regardless of whitelist
        const res = await api.get('/api/connectors/platforms/all');
        setPlatforms(res.data.platforms || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load platforms');
      } finally {
        setLoading(false);
      }
    };
    fetchPlatforms();
  }, [open, company]);

  if (!company) return null;

  const handleToggle = (name) => {
    setSelected(prev => prev.includes(name)
      ? prev.filter(k => k !== name)
      : [...prev, name]);
  };

  const handleSelectAll = () => setSelected(platforms.map(p => p.name));
  const handleClear = () => setSelected([]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/api/users/admin/companies/${company.id}/platforms`, {
        enabled_platforms: selected
      });
      if (onSaved) onSaved({ ...company, enabled_platforms: selected });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Group platforms by category for readability (mirrors module groups)
  const grouped = platforms.reduce((acc, p) => {
    const cat = p.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});
  const categoryOrder = Object.keys(grouped).sort();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Edit Connectors — {company.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Empty selection = no restriction (company sees all connectors).
          Non-empty = whitelist; users in this company can only ever see the connectors on this list
          in the "Add New Connector" screen.
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll} disabled={saving}>
                Select all
              </Button>
              <Button size="small" variant="outlined" onClick={handleClear} disabled={saving}>
                Clear (no restriction)
              </Button>
            </Box>

            {categoryOrder.map(cat => (
              <React.Fragment key={cat}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600, color: 'text.secondary',
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    mt: 1, display: 'block'
                  }}
                >
                  {cat}
                </Typography>
                <FormGroup>
                  <Grid container>
                    {grouped[cat].map(p => (
                      <Grid item xs={4} key={p.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selected.includes(p.name)}
                              onChange={() => handleToggle(p.name)}
                              disabled={saving}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">{p.display_name || p.name}</Typography>}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </React.Fragment>
            ))}
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyConnectorsDialog;
